import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from '../../auth/context/useAuth';
import {
  addSessionForUser,
  deleteSessionForUser,
  subscribeToSessions,
} from './session-api';

const SessionContext = createContext(null);

export function SessionProvider({ children }) {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setSessions([]);
      setLoading(false);
      return;
    }

    const unsubscribe = subscribeToSessions(user.uid, (fetchedSessions) => {
      setSessions(fetchedSessions);
      setLoading(false);
    });

    return unsubscribe;
  }, [user]);

  const addSession = useCallback(async (sessionData) => {
    if (!user?.uid) {
      throw new Error('You must be logged in to add a session.');
    }
    await addSessionForUser(user.uid, sessionData);
  }, [user]);

  const deleteSession = useCallback(async (sessionId) => {
    if (!user?.uid) {
      throw new Error('You must be logged in to delete a session.');
    }
    await deleteSessionForUser(user.uid, sessionId);
  }, [user]);

  const convertHoursToTime = (hours) => {
    if (!hours || Number.isNaN(hours)) return '';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const calculatePLPerHour = (profitLoss, sessionHours) => {
    if (!profitLoss || !sessionHours) return '';
    return (profitLoss / sessionHours).toFixed(2);
  };

  const convertTimeToHours = (time) => {
    if (!time || typeof time !== 'string' || !time.includes(':')) return 0;
    const [hours, minutes] = time.split(':').map(Number);
    return hours + minutes / 60;
  };

  const value = {
    sessions,
    loading,
    addSession,
    deleteSession,
    convertHoursToTime,
    calculatePLPerHour,
    convertTimeToHours,
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return ctx;
}
